/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useReducer, useState } from 'react'
import { usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { FixedSizeList as List } from 'react-window'
import CN from 'classnames'

import { Button } from 'components/atoms/Button'
import { CheckBox } from 'components/atoms/Checkbox'

const Table = ({
  columns,
  data,
  selectAll,
  setFetchAll,
  setSelectedRowIds,
  selectedRowIds,
  setTableVariables,
  tableVariables,
  totalRowCount,
  setUpdateCarrier,
  setIsLoading,
  pageNumber,
  setPageNumber,
  setIsUpdatePackageModalOpen,
  setIsUpdateCourrierModalOpen,
}) => {
  const [currentRow, setCurrentRow] = useState(null)
  const [checkAll, setCheckAll] = useState(false)
  const [, forceUpdate] = useReducer((x) => x + 1, 0)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 10,
        pageIndex: 0,
        selectedRowIds: selectedRowIds || [],
      },
      manualPagination: true,
      manualSortBy: false,
      autoResetPage: false,
      autoResetSelectedRows: false,
      disableSortBy: false,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((cols) => [
        {
          id: 'selection',
          customWidth: 32,
          Header: ({ getToggleAllPageRowsSelectedProps }) => {
            const { checked, onChange, ...rest } =
              getToggleAllPageRowsSelectedProps()
            return (
              <CheckBox
                {...rest}
                checked={checked}
                onChange={(e) => {
                  setIsLoading(true)
                  const isChecked = e.target.checked
                  setFetchAll(isChecked)
                  setCheckAll(isChecked)
                  handleCheckAll(isChecked)
                  setPageNumber(1)
                  setIsLoading(false)
                }}
              />
            )
          },
          Cell: ({ row }) => {
            const { checked, ...rest } = row.getToggleRowSelectedProps()
            return (
              <CheckBox
                {...rest}
                checked={checked}
                onChange={() => handleRowSelection(row)}
              />
            )
          },
        },
        ...cols,
      ])
    },
  )

  const handleRowSelection = (row) => {
    if (!row || !row.original || typeof row.original.id === 'undefined') return
    setCurrentRow(row)
    setSelectedRowIds((prevSelected) => {
      const isSelected = prevSelected.some((r) => r.id === row.original.id)
      if (isSelected) {
        row.toggleRowSelected(false)
        return prevSelected.filter((r) => r.id !== row.original.id)
      } else {
        row.toggleRowSelected(true)
        return [...prevSelected, row.original]
      }
    })
    forceUpdate()
  }

  useEffect(() => {
    if (selectedRowIds.length > 0 && page.length > 0) {
      page.forEach((row) => {
        const isSelected = selectedRowIds.some(
          (r) => String(r.id) === String(row.original.id),
        )
        row.toggleRowSelected(isSelected)
      })
    } else {
      toggleAllRowsSelected(false)
    }
  }, [page, selectedRowIds])

  const handleCheckAll = (isChecked) => {
    if (isChecked) {
      toggleAllRowsSelected(true)
    } else {
      toggleAllRowsSelected(false)
      setSelectedRowIds([])
    }
  }

  const [paginationVariables, setPaginationVariables] = useState({
    start: 0,
    end: 0,
  })

  useEffect(() => {
    setPaginationVariables({
      start: Number(tableVariables.take) * (pageNumber - 1) + 1,
      end: Math.min(Number(tableVariables.take) * pageNumber, totalRowCount),
    })
  }, [tableVariables, totalRowCount, pageNumber])

  useEffect(() => {
    setTableVariables((prev) => ({
      ...prev,
      skip: pageNumber === 1 ? 0 : Number(prev.take) * (pageNumber - 1),
    }))
  }, [pageNumber])

  const renderCustomCell = (cell, row) => {
    if (cell.column.id === 'dimension') {
      const pkg = row.original?.shipments?.[0]?.packages?.[0]
      return pkg ? (
        `${pkg.height} * ${pkg.width} * ${pkg.length}`
      ) : (
        <button
          onClick={() => {
            setSelectedRowIds([row.original])
            setIsUpdatePackageModalOpen(true)
          }}
          style={buttonStyle}>
          Update
        </button>
      )
    }
    if (cell.column.id === 'carrierType') {
      const carrier = row.original?.partnerOrder
      return carrier?.defaultCarrierName && carrier?.carrierProduct ? (
        `${carrier.defaultCarrierName} - ${carrier.carrierProduct}`
      ) : (
        <button
          onClick={() => {
            setSelectedRowIds([row.original])
            setIsUpdateCourrierModalOpen(true)
          }}
          style={buttonStyle}>
          Update
        </button>
      )
    }
    return cell.render('Cell')
  }

  const buttonStyle = {
    backgroundColor: '#F1A248',
    color: 'white',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '5px',
    cursor: 'pointer',
  }

  return (
    <div className='styled-scroll h-full relative overflow-y-auto rounded border bg-white border-N-200'>
      <table className='w-full' {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={CN(
                    'bg-N-100 border-b border-b-N-200 sticky top-0 shadow-sm text-left pl-4 z-[1]',
                    { 'bg-N-200': column.isSorted },
                  )}
                  style={{ height: 40 }}
                  width={column.customWidth}>
                  <div
                    className='inline-flex items-center'
                    style={{ minHeight: 20 }}>
                    {column.render('Header')}
                    <span className='flex ml-1' style={{ width: 14 }}>
                      {column.isSorted &&
                        (column.isSortedDesc ? (
                          <i className='ri-arrow-up-s-line' />
                        ) : (
                          <i className='ri-arrow-down-s-line' />
                        ))}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row)
            return (
              <tr className='h-11 cursor-pointer' {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    className={CN(
                      'border-b border-b-N-200 text-sm text-N-700 py-[11px] px-4',
                      {
                        'bg-N-300 border-b border-b-N-400': cell.isSelected,
                      },
                    )}>
                    {renderCustomCell(cell, row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className='flex items-center justify-between px-3 py-[10px] border border-N-200 rounded-b-md overflow-auto'>
        {!selectAll && (
          <div className='flex items-center'>
            <span className='text-sm text-N-700'>Show rows per page</span>
            <select
              className='w-20 ml-2 border rounded-md outline-none border-N-200 h-7'
              value={tableVariables.take}
              onChange={(e) => {
                setPageNumber(1)
                setTableVariables({ ...tableVariables, take: e.target.value })
              }}>
              {[10, 20, 30, 50].map((pageSizeValue) => (
                <option key={pageSizeValue} value={pageSizeValue}>
                  {pageSizeValue}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className='flex items-center text-sm text-N-700'>
          Selected Row Count - {selectedRowIds.length}
        </div>

        <div className='flex'>
          <div className='flex items-center text-sm text-N-700 mr-9'>
            <p>
              {paginationVariables.start} - {paginationVariables.end} of{' '}
              {totalRowCount}
            </p>
          </div>
          <Button
            className='mr-8'
            appearance='dull'
            size='xs'
            onClick={() => setPageNumber(pageNumber - 1)}
            disabled={pageNumber <= 1}>
            <i className='ri-arrow-left-s-line' />
          </Button>
          <Button
            className='mr-2'
            appearance='dull'
            size='xs'
            onClick={() => setPageNumber(pageNumber + 1)}
            disabled={pageNumber * tableVariables.take >= totalRowCount}>
            <i className='ri-arrow-right-s-line' />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Table
